'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { updateSession } from '../actions';
import { Session, ValidatedSession } from '../types';
import { AuthProviderProps } from './auth-provider';
export type SessionStatus = 'updating' | 'needsValidation' | 'authenticated' | 'unauthenticated';

/**
 * Valor expuesto por el contexto de autenticación.
 */
export interface AuthContextValue {
  data: ValidatedSession | null;
  status: SessionStatus;
  update: (_user: Partial<Session>) => Promise<Session | null>;
}
const AuthContext = createContext<AuthContextValue | null>(null);
type AuthProviderInternalProps = AuthProviderProps & {
  session: Session | null;
};

/**
 * Provider interno de cliente que mantiene el estado de sesión.
 */
export function AuthProviderInternal({
  children,
  session
}: AuthProviderInternalProps) {
  const [currentSession, setCurrentSession] = useState<Session | null>(session);
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    setCurrentSession(session);
  }, [session]);
  const status: SessionStatus = useMemo(() => {
    if (isUpdating) return 'updating';
    if (!currentSession) return 'unauthenticated';
    if ('needsValidation' in currentSession) return 'needsValidation';
    return 'authenticated';
  }, [currentSession, isUpdating]);
  const update = useCallback(async (newPartialUser: Partial<Session>) => {
    if (!currentSession) return null;
    const nextSession = {
      ...currentSession,
      ...newPartialUser
    } as Session;
    setIsUpdating(true);
    try {
      setCurrentSession(nextSession);
      await updateSession(nextSession);
      return nextSession;
    } catch (error) {
      setCurrentSession(currentSession);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [currentSession]);
  const value = useMemo(() => ({
    data: (status === 'needsValidation' ? null : currentSession) as ValidatedSession,
    status,
    update
  }), [currentSession, status, update]);
  return <AuthContext value={value}>{children}</AuthContext>;
}

/**
 * Acceso al contexto de autenticación.
 *
 * @throws Error si se usa fuera de `AuthProvider`.
 */
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}